import { cn } from '@cortex/ui';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { X } from 'lucide-react';
import type * as React from 'react';

/** The message of an API error body, else a status line. */
function errorMessage(error: unknown, response?: Response): string {
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string')
    return error.message;
  return response ? `The API answered ${response.status}.` : 'The API is unreachable.';
}

// What the Agents and agent pages share: the old pages' button and field classes, a modal, and the
// api-result unwrap.

export const btnGhost =
  'inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 disabled:opacity-50 disabled:cursor-not-allowed';
export const btnPrimary =
  'inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed';
export const btnDanger =
  'inline-flex items-center gap-1.5 rounded-md bg-red-600 text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50';
export const input =
  'w-full rounded-md border border-input-border bg-card-bg text-page-text text-sm px-2.5 py-1.5 outline-none focus:border-nav-active';
export const iconBtn =
  'text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1 rounded inline-flex items-center';

/** The track and knob of a switch; the checkbox with `role="switch"` beside it (`sr-only`) holds the state. */
export function SwitchTrack({ on, dim = false }: { on: boolean; dim?: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative h-5 w-9 shrink-0 rounded-full transition-colors',
        on ? 'bg-nav-active' : 'bg-card-border',
        dim ? 'opacity-60' : '',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all',
          on ? 'left-4' : 'left-0.5',
        )}
      />
    </span>
  );
}

export async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  let r;
  try {
    r = await p;
  } catch {
    throw new Error('The API is unreachable.');
  }
  if (r.error !== undefined || (r.data === undefined && r.response.status !== 204))
    throw new Error(errorMessage(r.error, r.response));
  return r.data as T;
}

export const messageOf = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
  testId,
}: {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Wider than a form's: `true` a wide panel, `'lg'` a roomy dialog. */
  wide?: boolean | 'lg';
  testId?: string;
}) {
  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto pointer-events-none">
          <DialogPrimitive.Content
            aria-describedby={undefined}
            data-testid={testId}
            className={`pointer-events-auto bg-card-bg text-page-text rounded-[8px] shadow-xl w-full ${wide === 'lg' ? 'max-w-xl' : wide ? 'max-w-3xl' : 'max-w-md'} mt-12 md:mt-0 outline-none`}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-divider">
              <DialogPrimitive.Title className="text-lg font-semibold flex items-center gap-2">
                {title}
              </DialogPrimitive.Title>
              <DialogPrimitive.Close className={iconBtn} aria-label="Close">
                <X size={18} />
              </DialogPrimitive.Close>
            </div>
            <div className="px-5 py-4">{children}</div>
            {footer ? (
              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-divider">
                {footer}
              </div>
            ) : null}
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
